import { useCallback, useEffect, useState } from 'react';
import { Button, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import useIdbSync from 'src/services/idbSync/useIdbSync';
import { ExamStepEnum } from 'src/services/offline/db';
import {
  addRetakeExamIDB,
  copyOldEyeImageToExamIDB,
  countCheckedImagesIDB,
  setOrderLastStepIDB,
} from 'src/services/offline/outboxClient';
import {
  captureExamEvent,
  EXAM_EVENTS,
  EXAM_STEP,
  ExamPauseSource,
  RETAKE_PHASE,
} from 'src/services/posthogEvents';
import { v4 as uuidv4 } from 'uuid';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import AnalyzingProgressModal from 'src/components/AnalyzingProgressModal/AnalyzingProgressModal';
import CameraConnectionIssueController from 'src/components/CameraConnectionIssueModals/CameraConnectionIssueController';
import PauseExamModal from 'src/components/ConnectionIssueModals/PauseExamModal';
import PatientInfoWithControls from 'src/components/custom_components/PatientInfoWithControls';

import { Api } from 'src/models/Api.model';
import { Eye, EyePropertyCode } from 'src/models/Eye.model';
import { CancelStatus } from 'src/models/GalenData/CancelStatus.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { I18NextNS } from 'src/models/Language.model';
import { EyesPosition } from 'src/models/Session.model';

import useCancelCaptureImages from 'src/hooks/camera/useCancelCaptureImages';
import useCaptureImage from 'src/hooks/camera/useCaptureImage';
import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';

import { captureError } from 'src/utils/errorReporting';

import CameraPreviewColumn from './CameraPreviewColumn';
import ImagePreviewColumn from './ImagePreviewColumn';
import SmallPupilModal from './SmallPupilModal';

const SessionPreview = () => {
  const {
    leftUrl,
    rightUrl,
    setLeftUrl,
    setRightUrl,
    cameraData,
    session,
    updateSession,
    resetSession,
    isCapturing,
    setIsCapturing,
    isCanceling,
    setIsCanceling,
    setHasStarted,
    setBackendStatus,
    setLeftImageSubmission,
    setRightImageSubmission,
    cameraConnectionIssueReason,
    setCameraConnectionIssueReason,
    captureInterruptedByConnectionIssue,
    setCaptureInterruptedByConnectionIssue,
    setLeftImageAttempts,
    setRightImageAttempts,
  } = useRemoteBoundStore();

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { userId, practiceId } = useGetCurrentUser();
  const { getErrorMessage } = useGetGalenErrorMsg();

  const location = useLocation<{
    isResume: boolean;
    openAnalysis?: boolean;
  }>();

  const isResume = location.state?.isResume || false;

  useEffect(() => {
    if (!session.orderId) {
      return;
    }
    setOrderLastStepIDB(session.orderId, ExamStepEnum.IMAGING);
  }, [session.orderId]);

  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [isShowAnalyzingModal, setIsShowAnalyzingModal] = useState(false);
  const [cancelStatus, setCancelStatus] = useState<CancelStatus>(
    CancelStatus.NotCanceled,
  );
  const [showPauseExamModal, setShowPauseExamModal] = useState(false);

  const { updateExamFields, restartSync, cancelSync } = useIdbSync();

  const handleCloseAnalyzingModal = () => {
    setIsShowAnalyzingModal(false);
  };

  const handleSubmitSession = async () => {
    if (!session.orderId) {
      throw new Error(
        t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
      );
    }

    if (!practiceId) {
      throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
    }

    if (!userId) {
      throw new Error(
        t('message.error.userIdNotFound', {
          ns: I18NextNS.COMMON,
        }),
      );
    }

    await updateExamFields(session.orderId, session.iid, {
      SubmittedForAnalysis: true,
      CameraSn: cameraData?.deviceSN,
      CameraVersion: cameraData?.versionInfo.version,
      PracticeUserId: userId,
    });

    const [leftCheckedImages, rightCheckedImages] = await countCheckedImagesIDB(
      session.orderId,
    );
    setLeftImageSubmission(leftCheckedImages);
    setRightImageSubmission(rightCheckedImages);
    setIsAnalyzing(true);
    setIsShowAnalyzingModal(true);
    setBackendStatus(undefined);
  };

  const { handleCancelCapture } = useCancelCaptureImages();

  const handleCancel = () => {
    Api.confirmBox({
      title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
      message: t('newExam.confirmExitImaging'),
      callback: (result) => {
        if (!result) {
          return;
        }
        setCancelStatus(CancelStatus.BothEyesCanceled);
        handleCancelCapture();
      },
      locale: language,
    });
  };

  const reopenAnalysis = useCallback(async () => {
    if (!location.state?.openAnalysis) {
      return;
    }
    setIsShowAnalyzingModal(true);
    if (!session.graded && session.orderId) {
      setIsAnalyzing(true);
      await restartSync(session.orderId);
    }
  }, [location.state?.openAnalysis, restartSync, session]);

  useEffect(() => {
    if (!isResume) return;

    setIsCapturing(false);
    setIsCanceling(false);
    setHasStarted(false);

    reopenAnalysis();
    // We only want to run this effect when the component mounts, not when the component updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { captureLeftImage, captureRightImage, captureBothImages } =
    useCaptureImage();

  const handleRetakeCapture = (position: EyesPosition) => {
    setCaptureInterruptedByConnectionIssue(false);
    if (position === EyesPosition.BOTH) {
      captureBothImages();
      setCancelStatus(CancelStatus.NotCanceled);
    }
    if (position === EyesPosition.LEFT) {
      setCancelStatus(CancelStatus.RightEyeCanceled);
      captureLeftImage();
    }
    if (position === EyesPosition.RIGHT) {
      setCancelStatus(CancelStatus.LeftEyeCanceled);
      captureRightImage();
    }
  };

  const handleRetakeBothEyes = async () => {
    captureExamEvent(EXAM_EVENTS.RETAKE_REQUESTED, {
      target: EyesPosition.BOTH,
      phase: RETAKE_PHASE.PRE_ANALYSIS,
    });
    await handleRetake(EyesPosition.BOTH);
  };

  const handleRetakeLeftEye = async () => {
    captureExamEvent(EXAM_EVENTS.RETAKE_REQUESTED, {
      target: EyesPosition.LEFT,
      phase: RETAKE_PHASE.PRE_ANALYSIS,
    });
    await handleRetake(EyesPosition.LEFT);
  };

  const handleRetakeRightEye = async () => {
    captureExamEvent(EXAM_EVENTS.RETAKE_REQUESTED, {
      target: EyesPosition.RIGHT,
      phase: RETAKE_PHASE.PRE_ANALYSIS,
    });
    await handleRetake(EyesPosition.RIGHT);
  };

  const handleRetake = async (position: EyesPosition) => {
    if (isCapturing || !session.orderId) {
      return;
    }
    setIsCapturing(true);

    try {
      const orderId = session.orderId;
      const oldExamId = session.iid;
      const newExamId = uuidv4();

      setOrderLastStepIDB(orderId, ExamStepEnum.IMAGING).catch(() => {});

      const resetGradingState = {
        iid: newExamId,
        graded: false,
        leftResults: [],
        rightResults: [],
      };

      if (position === EyesPosition.BOTH) {
        setLeftUrl('');
        setRightUrl('');

        await addRetakeExamIDB(orderId, oldExamId, newExamId);
        updateSession(resetGradingState);
        handleRetakeCapture(position);
        return;
      }

      if (position === EyesPosition.LEFT) {
        setLeftUrl('');

        await addRetakeExamIDB(orderId, oldExamId, newExamId);
        updateSession(resetGradingState);

        await copyOldEyeImageToExamIDB(
          orderId,
          oldExamId,
          newExamId,
          EyePropertyCode.RIGHT,
        );
        handleRetakeCapture(position);
        return;
      }

      if (position === EyesPosition.RIGHT) {
        setRightUrl('');

        await addRetakeExamIDB(orderId, oldExamId, newExamId);
        updateSession(resetGradingState);

        await copyOldEyeImageToExamIDB(
          orderId,
          oldExamId,
          newExamId,
          EyePropertyCode.LEFT,
        );
        handleRetakeCapture(position);
      }
    } catch (error) {
      captureError(error, 'Failed to retake exam images');
      setIsCapturing(false);
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      setLeftUrl('');
      setRightUrl('');
      setLeftImageAttempts(0);
      setRightImageAttempts(0);
      setLeftImageSubmission(0);
      setRightImageSubmission(0);
      setCameraConnectionIssueReason(undefined);
      setCaptureInterruptedByConnectionIssue(false);
      cancelSync();
    };
  }, [
    setLeftUrl,
    setRightUrl,
    setCameraConnectionIssueReason,
    setCaptureInterruptedByConnectionIssue,
    cancelSync,
    setLeftImageAttempts,
    setRightImageAttempts,
    setLeftImageSubmission,
    setRightImageSubmission,
  ]);

  return (
    <div className="d-flex flex-column h-100 overflow-hidden">
      <PatientInfoWithControls />
      <div
        className="d-flex gap-3 flex-fill overflow-auto"
        style={{ minHeight: 0 }}
      >
        <CameraPreviewColumn isExpanded={!!(leftUrl && rightUrl)} />
        <ImagePreviewColumn
          imageUrl={leftUrl}
          eyeType={Eye.LEFT}
          onRetake={() => {
            handleRetakeLeftEye();
          }}
          retakeLabel={t('newExam.retakeLeftEye')}
          isExpanded={!!(leftUrl && rightUrl)}
          isRetakeDisabled={isCapturing}
          isCanceling={isCanceling}
          hasCanceled={
            captureInterruptedByConnectionIssue ||
            cancelStatus === CancelStatus.LeftEyeCanceled ||
            cancelStatus === CancelStatus.BothEyesCanceled
          }
        />
        <ImagePreviewColumn
          imageUrl={rightUrl}
          eyeType={Eye.RIGHT}
          onRetake={() => {
            handleRetakeRightEye();
          }}
          retakeLabel={t('newExam.retakeRightEye')}
          isExpanded={!!(leftUrl && rightUrl)}
          isRetakeDisabled={isCapturing}
          isCanceling={isCanceling}
          hasCanceled={
            captureInterruptedByConnectionIssue ||
            cancelStatus === CancelStatus.RightEyeCanceled ||
            cancelStatus === CancelStatus.BothEyesCanceled
          }
        />
      </div>
      <Row style={{ marginTop: 'auto' }}>
        <Stack direction="horizontal" gap={3} className="d-flex pt-2">
          <div className="d-flex gap-3">
            <Button
              variant="danger"
              disabled={!isCapturing || isCanceling}
              onClick={() => {
                handleCancel();
              }}
              data-track="exam.session-preview.cancel-imaging"
              data-test="cancel-imaging"
              data-observe
            >
              {isCanceling
                ? t('newExam.cancelingImaging')
                : t('newExam.cancelImaging')}
            </Button>
          </div>
          <div className="ms-auto d-flex gap-3">
            <Button
              variant="primary-100"
              onClick={() => {
                handleRetakeBothEyes();
              }}
              disabled={isCapturing || isCanceling}
              data-track="exam.session-preview.retake-both-eyes"
              data-test="retake-both-eyes"
              data-observe
            >
              {t('newExam.retake')}
            </Button>
            <Button
              variant="primary"
              className="w-200px shadow"
              onClick={() => {
                handleSubmitSession();
              }}
              data-track="exam.session-preview.submit-session"
              data-test="submit-exam"
              data-observe
              disabled={!leftUrl || !rightUrl}
            >
              {t('newExam.checkImage')}
            </Button>
          </div>
        </Stack>
      </Row>

      {isShowAnalyzingModal && (
        <AnalyzingProgressModal
          isAnalyzing={isAnalyzing}
          setIsAnalyzing={setIsAnalyzing}
          handleRetake={handleRetake}
          examId={session.iid}
          handleCloseAnalyzingProgressModal={handleCloseAnalyzingModal}
          handleResetSession={resetSession}
        />
      )}
      <SmallPupilModal />
      {cameraConnectionIssueReason && !isShowAnalyzingModal && (
        <CameraConnectionIssueController
          show={!showPauseExamModal}
          onConnectionRestored={() => setCameraConnectionIssueReason(undefined)}
          showRetakeInstruction
          secondaryButton={
            <Button
              variant="outline-gray-500"
              className="button-slim clear-exam-button"
              data-test="camera-connection-issue-pause-exam"
              onClick={() => {
                captureExamEvent(EXAM_EVENTS.EXAM_PAUSE_INITIATED, {
                  source: ExamPauseSource.CAMERA_CONNECTION,
                  step: EXAM_STEP.IMAGING,
                });
                setShowPauseExamModal(true);
              }}
            >
              {t('connectionIssue.pauseExamModal.pauseExam')}
            </Button>
          }
        />
      )}
      <PauseExamModal
        show={showPauseExamModal}
        description={t('connectionIssue.pauseExamModal.cameraDescription')}
      />
    </div>
  );
};

export default SessionPreview;
